import React, { useState, useEffect } from "react";
import { TextField, Grid, Typography, Divider, Box, Button, InputAdornment, useMediaQuery, CircularProgress } from "@material-ui/core";
import PropTypes from "prop-types";
import EditIcon from "@material-ui/icons/Edit";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import { useStyles } from "./styles";
import { Validation } from "../../../resources/validation";
import InputMediaCard from "../../ui-elements/inputCards";
import RatingSlider from "../../ui-elements/ratingSlider";
import { OnePerson, TwoPerson, ThreePerson, FivePerson, Solar, Battery, Pool } from "../../../resources/images/calculator/svgImages";
import UseDebounce from "../../../resources/Debounce";
import { Utils } from "../../../resources/util";

export default function CostCalculator(props) {
  const mobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [postalCode, setPostalCode] = useState(props.postalCode ? props.postalCode : "");
  const [error, setError] = useState("");
  const classes = useStyles();
  const [amountVisible, setAmountVisible] = useState(false);
  const [values, setValues] = useState({
    peopleCount: [1],
    items: [],
    amount: "",
    wasteFactor: 4
  });
  const min = props.sliderProps.min;
  const max = props.sliderProps.max;
  const defaultVal = props.sliderProps.default;
  const marks = props.sliderProps.marks;
  const step = props.sliderProps.step;
  const deBounceVal = UseDebounce(values, 500);
  const persons = [
    {
      value: 1,
      header: mobile && "1",
      label: mobile ? "Person" : "1 Person",
      icon: <OnePerson cardImage={`${classes.cardImage} ${values.peopleCount.includes(1) && classes.cardImageActive}`} />
    },
    {
      value: 2,
      header: mobile && "2",
      label: mobile ? "People" : "2 People",
      icon: <TwoPerson cardImage={`${classes.cardImage} ${values.peopleCount.includes(2) && classes.cardImageActive}`} />
    },
    {
      value: 3,
      header: mobile && "3-4",
      label: mobile ? "People" : "3-4 People",
      icon: <ThreePerson cardImage={`${classes.cardImage} ${values.peopleCount.includes(3) && classes.cardImageActive}`} />
    },
    {
      value: 5,
      header: mobile && "5+",
      label: mobile ? "People" : "5+ People",
      icon: <FivePerson cardImage={`${classes.cardImage} ${values.peopleCount.includes(5) && classes.cardImageActive}`} />
    }
  ];
  const items = [
    {
      value: "solar",
      label: "Solar",
      icon: <Solar cardImage={`${classes.cardImage} ${values.items.includes("solar") && classes.cardImageActive}`} />
    },
    {
      value: "battery",
      label: "Battery",
      icon: <Battery cardImage={`${classes.cardImage} ${values.items.includes("battery") && classes.cardImageActive}`} />
    },
    {
      value: "pool",
      label: "Pool",
      icon: <Pool cardImage={`${classes.cardImage} ${values.items.includes("pool") && classes.cardImageActive}`} />
    }
  ];

  useEffect(() => {
    props.averageBillCal(values.peopleCount, values.items, values.wasteFactor);
    // eslint-disable-next-line
  }, [props.didMound]);

  useEffect(() => {
    props.averageBillCal(values.peopleCount, values.items, values.wasteFactor);
    // eslint-disable-next-line
  }, [deBounceVal]);

  const onSelectPeople = value => {
    const newCount = [];
    newCount.push(value);
    setValues({
      ...values,
      peopleCount: newCount
    });
  };

  const onSelectItems = value => {
    const newCount = values.items;
    if (newCount.includes(value)) {
      newCount.splice(newCount.indexOf(value), 1);
    } else {
      newCount.push(value);
    }
    setValues({
      ...values,
      items: newCount
    });
  };

  const changeAmount = e => {
    if (e.target.value === "") {
      setError("This field can not be empty");
      setValues({
        ...values,
        amount: e.target.value
      });
    } else {
      if (!Validation.number(e.target.value)) {
        setError("Amount shoud be a number");
      } else {
        setValues({
          ...values,
          amount: e.target.value
        });
        setError("");
      }
    }
  };

  const amountVisibility = () => {
    setAmountVisible(!amountVisible);
  };

  const sliderOnChange = value => {
    setValues({
      ...values,
      wasteFactor: value
    });
  };

  const handleChange = event => {
    if (event.target.value === "") {
      setError("This field can not be empty");
      setPostalCode(event.target.value);
    } else {
      if (!Validation.number(event.target.value)) {
        setError("Postcode shoud be a number");
      }
      if (Validation.number(event.target.value) && event.target.value.length < 5) {
        setPostalCode(event.target.value);
        setError("");
        if (event.target.value.length === 4) {
          props.setPostal(event.target.value);
        }
      }
    }
  };

  const goToPackages = () => {
    const amount = values.amount ? values.amount : props.estimatedAnnualBill;
    
    if (props.noFnPayBgt === 'true') {
      props.history.push({
        pathname: "/select-package",
        search: `?postcode=${postalCode}&amount=${amount}`,
        state: {
          showFortnightlyPayments: props.showFn,
          postalCode: postalCode
        }
      });
    } else {
      props.history.push({
        pathname: "/nrg-packages",
        search: `?postcode=${postalCode}&amount=${amount}`,
        state: {
          showFortnightlyPayments: props.showFn,
          postalCode: postalCode
        }
      });
    }
  };

  return (
    <Grid className={classes.root} container direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
      <Grid item xs={12} className={`${classes.section} ${classes.postalInput}`}>
        <TextField
          autoComplete="on"
          error={error !== ""}
          helperText={error}
          value={postalCode}
          onChange={handleChange}
          required
          type="num"
          name="postalcode"
          id="postalcode"
          label="Your postcode"
          margin="normal"
          variant="outlined"
          className={classes.postalDisplay}
          color="secondary"
        />
      </Grid>
      <Grid item xs={12} className={classes.section}>
        <Grid item xs={12} container direction="row" justifyContent="space-evenly" alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6" component="h6" className={classes.headerTitles}>
              How many people do you have at home ?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
              {persons.map((valueObj, index) => (
                <Grid key={index} item>
                  <InputMediaCard
                    icon={valueObj.icon}
                    title={valueObj.label}
                    mainTitle={mobile && valueObj.header}
                    class={classes.user1}
                    onClick={() => onSelectPeople(valueObj.value)}
                    selected={values.peopleCount.includes(valueObj.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.section}>
        <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6" component="h6" className={classes.headerTitles}>
              Do you have ?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
              {items.map((valueObj, index) => (
                <Grid key={index} item>
                  <InputMediaCard
                    icon={valueObj.icon}
                    title={valueObj.label}
                    cardInput={classes.itemCards}
                    class={classes.user1}
                    onClick={() => onSelectItems(valueObj.value)}
                    selected={values.items.includes(valueObj.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={`${classes.section} ${classes.endCalc}`}>
        <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6" component="h6" className={classes.headerTitles}>
              What best describes you ?
            </Typography>
          </Grid>
          <Grid item xs={12} className={classes.sliderWrapper}>
            <RatingSlider
              marks={marks}
              min={min}
              max={max}
              step={step}
              default={defaultVal}
              sliderOnChange={sliderOnChange}
              labelStart={"I'm super tight with my electricity"}
              labelEnd={"I leave everything on all the time\n(Don't worry we won't judge you)"}
              valueLabel={"off"}
            />
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Divider variant="middle" className={`${classes.divider} ${classes.section}`} />
      <Grid item xs={12} className={classes.section}>
        <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6" component="h6" className={classes.headerTitles}>
              Sounds like your average Quarterly Power Bill would be around
            </Typography>
            <Box className={classes.predictedAmount}>
              <Typography variant="h4" component="h4">
                $ {Utils.formatCurrency(props.estimatedAnnualBill)}
              </Typography>
            </Box>
            <Typography variant="subtitle1" gutterBottom>
              Click on ‘Change Bill Amount’ if the estimated bill amount does not match your bill
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {amountVisible && (
              <TextField
                required
                autoComplete="on"
                error={error !== ""}
                helperText={error}
                value={values.amount}
                onChange={changeAmount}
                name="price"
                id="price"
                label="Quarterly Cost"
                className={classes.textField}
                margin="dense"
                variant="outlined"
                color="secondary"
                InputProps={{
                  className: classes.input,
                  startAdornment: (
                    <InputAdornment className={classes.adornment} position="start">
                      $
                    </InputAdornment>
                  )
                }}
              />
            )}
            <Grid item xs={12}>
              <Button
                onClick={amountVisibility}
                variant="contained"
                // color="default"
                className={classes.changeButton}
                disabled={values.amount !== ""}
                startIcon={<EditIcon />}
              >
                Change bill amount
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => goToPackages()}
              // onClick={() => props.getBenefitsAndPayments(values.amount)}
              className={classes.continueButton}
              endIcon={<NavigateNextIcon />}
              size="large"
              disabled={props.benefitsFetching}
            >
              Calculate
              {props.benefitsFetching && <CircularProgress size={24} className={classes.buttonProgress} />}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

CostCalculator.propTypes = {
  setPostal: PropTypes.func,
  sliderProps: PropTypes.object,
  postalCode: PropTypes.oneOfType([PropTypes.string || PropTypes.number]),
  averageBillCal: PropTypes.func,
  estimatedAnnualBill: PropTypes.oneOfType([PropTypes.string || PropTypes.number]),
  didMound: PropTypes.bool,
  getBenefitsAndPayments: PropTypes.func,
  benefitsFetching: PropTypes.bool,
  myDivToFocus: PropTypes.any
};
